import React, { useEffect, useState } from "react";
import { Form, Formik, Field, useFormikContext } from "formik";
import { Modal, Button } from "react-bootstrap";
import LoaderButton from "../../../shared-components/Button/LoaderButton";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import Label from "../../../shared-components/Label/Label";
import { formatMessage } from "../../../translations/format-message";
import { FormattedMessage } from "react-intl";
import FormikFormTextArea from "../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import { IFotaAddSysAdmin } from "../../../models/Form/IFotaForm";
import FormikFormFileSizeInputField from "../../../shared-components/FormikFormHandlers/FormikFormFileSizeInputField";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import { DATE_FORMAT, FirmwareFileNameValidCharacters, fotaFileTypeOptions, getFileNameAndExtensionFromPath, MAX_FILE_SIZE } from "../../../constants/common-constants";
import moment from "moment";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import './fota-mzl-form.css';
import CustomTooltip from "../../../shared-components/Tooltip/tooltip";
import { validationSchemaEditSysAdmin, validationSchemaSysAdmin } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";

export interface IPropsSysAdmin {
  id: number;
  initialValues: IFotaAddSysAdmin;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  dataLoading: boolean;
  actionLoading: boolean;
}

const AddJetFotaForm = (props: IPropsSysAdmin) => {
  const [jetVersionFileContent, setJetVersionFileContent] = useState<string>("");

  const [selectedFileType, setSelectedFileType] = useState<any>(props.initialValues.FileType);
    
  const handleFileReadJetVersionFileAscii = (content: any) => {
    setJetVersionFileContent(content);
  };
  
  const handleFileReadJetVersionFileHex = (event: any) => {
    const content = event.target.result;
    setJetVersionFileContent(content);
  };

  const FormObserver: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<IFotaAddSysAdmin>();

    useEffect(() => {
      if (props.id > 0 && JSON.stringify(values) !== JSON.stringify(props.initialValues)) {
        if(values.WantToUpdateFile === "false"){
          setFieldValue("File", "");
          setFieldValue("FileName", "");
        }
      }
    }, [values.WantToUpdateFile]);

    useEffect(() => {
      if (selectedFileType != values.FileType) {
          setFieldValue("File", "");
          setSelectedFileType(values.FileType)
      }
    }, [values.FileType]);

    return null;
  }

  return (
    <Modal
      className=" fancy-modal"
      show={true}
      centered
      size="lg"
      onHide={() => { }}
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage
            id={props.id > 0 ? "fota_jet_update" : "fota_jet_add"}
          />
        </Modal.Title>
        <button
          onClick={(e) => props.onModalClosed()}
          disabled={props.actionLoading}
          className="Crossicons"
          title={formatMessage("close")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23"
            height="23"
            fill="currentColor"
            className="bi bi-x"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
      </Modal.Header>
      <Formik
        initialValues={props.initialValues}
        validationSchema={props.id > 0 ? validationSchemaEditSysAdmin : validationSchemaSysAdmin}
        enableReinitialize
        validateOnBlur={false}
        onSubmit={async (values: any) => {
          let fileDetails = getFileNameAndExtensionFromPath(values.File);
          let EffectiveDateTimestamp =
              new Date(values.EffectiveDateTimestamp.$d).toString() !== "Invalid Date"
                ? moment(values.EffectiveDateTimestamp.$d).format(DATE_FORMAT)
                : values.EffectiveDateTimestamp;
          let tempValues: IFotaAddSysAdmin = {
            ...values,
            EffectiveDateTimestamp: EffectiveDateTimestamp,
            FileName: (values.IsCustomFileNameWithExtension || values.IsCustomFileNameWithoutExtension) ? 
                values.FileName : ((props.id > 0 && jetVersionFileContent == "") ? 
                props.initialValues?.FileName : fileDetails.Name + fileDetails.Extension ),
            File: props.id > 0 ? (values.WantToUpdateFile === "true" ? jetVersionFileContent : "") 
                : jetVersionFileContent,
          };
          if(values.IsCustomFileNameWithExtension)
            tempValues.FileName = tempValues.FileName + fileDetails.Extension;
          if(tempValues.File?.length <= MAX_FILE_SIZE)
            props.submitHandler({ ...tempValues });
        }}
      >
        {({ setErrors, values, validateForm, handleSubmit, setFieldValue }) => {
          
          const handleCheck = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const field = event.target.name;
            const isChecked = event.target.checked;

            const errors = await validateForm();
            setErrors(errors);

            if (field === "IsCustomFileNameWithExtension") {
              if (isChecked) {
                setFieldValue("IsCustomFileNameWithExtension", true);
                setFieldValue("IsCustomFileNameWithoutExtension", false); // Uncheck the other checkbox
              } else {
                setFieldValue("IsCustomFileNameWithExtension", false);
              }
            } else if (field === "IsCustomFileNameWithoutExtension") {
              if (isChecked) {
                setFieldValue("IsCustomFileNameWithoutExtension", true);
                setFieldValue("IsCustomFileNameWithExtension", false); // Uncheck the other checkbox
              } else {
                setFieldValue("IsCustomFileNameWithoutExtension", false);
              }
            }
          };
          return (
            <Form className="user-form" onSubmit={handleSubmit}>
              <FormObserver />
              {(props.dataLoading || props.actionLoading) ? <FormLoader loading={(props.dataLoading || props.actionLoading)} /> : null}
              <Modal.Body>
                <div className="row">
                  <div className="form-group col-4">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="version"
                      />
                      <Field
                        className="form-control"
                        name="Version"
                        type="text"
                        validcharacter={["."]}
                        placeholder="version"
                        as={FormikFormInput}
                      />
                  </div>
                  <div className="form-group col-4">
                    <Label
                      className="label-style col-form-label"
                      required={true}
                      label="file_type"
                    />
                    <Field
                      className="form-control"
                      name="FileType"
                      placeholder="file_type"
                      options={fotaFileTypeOptions}
                      as={FormikFormSelect}
                    />
                  </div>
                  <div className="form-group col-4">
                  <Label
                      className="label-style me-2 col-form-label"
                      required={true}
                      label="effective_date"
                    />
                    <Field
                      className="form-control"
                      name="EffectiveDateTimestamp"
                      as={FormikDateFieldMUI}
                      disableFuture={false}
                      max={moment(props?.initialValues?.EffectiveDateTimestamp).add(6, "months").format(DATE_FORMAT)}
                      min={moment(props?.initialValues?.EffectiveDateTimestamp).format(DATE_FORMAT)}
                      ampm="false"
                    />
                  </div>
                  {(props.id > 0 && props.initialValues.FileContent) &&
                  (
                    <div className="form-group">
                      <div className="alert alert-warning">
                        <FormattedMessage id={"file_upload_message_fota"} />
                      </div>
                    </div>
                  )}
                  {
                    props.id > 0 &&
                    <div className="form-group row d-flex align-items-center">
                      <div className="col-md-4">
                        <Field
                          type="radio"
                          name="WantToUpdateFile"
                          value="false"
                          id="useOldFile"
                          className="form-check-input"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="useOldFile"
                        >
                          <FormattedMessage id="use_old_file" />
                        </label>
                      </div>
                      <div className="col-md-8">
                        <Field
                          type="radio"
                          name="WantToUpdateFile"
                          value="true"
                          id="WantToUpdateFile"
                          className="form-check-input"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="WantToUpdateFile"
                        >
                          <FormattedMessage id="provide_new_file" />
                        </label>
                      </div>
                    </div>
                  }
                  {
                    ((props.id > 0 && values.WantToUpdateFile === "true") ||
                    (props.id === -1)) &&
                    <>
                      <div className="form-group col-12">
                        <Label
                          className="label-style me-2 col-form-label"
                          required={true}
                          label="firmware_file"
                        />
                        <Field
                          name="File"
                          className="form-control"
                          showfilesizeerror="true"
                          filesize={MAX_FILE_SIZE}
                          selectedFileType={values.FileType}
                          handleFileUploadHex={handleFileReadJetVersionFileHex}
                          handleFileUploadAscii={handleFileReadJetVersionFileAscii}
                          as={FormikFormFileSizeInputField}
                        />
                      </div>   
                      <div className="form-group col-md-12 mt-1">
                        <div className="row">
                          <div className="col-md-2">
                            <label className="">
                              <FormattedMessage id="extension" />
                            </label>
                            <CustomTooltip id="custom_file_name_info" message="custom_file_name_info" />
                          </div>
                          <div className="col-md-5 form-check">
                            <Field
                              data-testid="IsCustomFileNameWithExtension"
                              className="form-check-input"
                              name="IsCustomFileNameWithExtension"
                              type="checkbox"
                              id="IsCustomFileNameWithExtension"
                              checked={values.IsCustomFileNameWithExtension}
                              onChange={handleCheck}
                            />
                            <label htmlFor="IsCustomFileNameWithExtension" className="form-check-label">
                              <FormattedMessage id="with_extension" />
                            </label>
                          </div>
                          <div className="col-md-5 form-check">
                            <Field
                              data-testid="IsCustomFileNameWithoutExtension"
                              className="form-check-input"
                              name="IsCustomFileNameWithoutExtension"
                              type="checkbox"
                              id="IsCustomFileNameWithoutExtension"
                              checked={values.IsCustomFileNameWithoutExtension}
                              onChange={handleCheck}
                            />
                            <label htmlFor="IsCustomFileNameWithoutExtension" className="form-check-label">
                              <FormattedMessage id="without_extension" />
                            </label>
                          </div>
                        </div>
                        <Field
                          disabled={!(values.IsCustomFileNameWithExtension || values.IsCustomFileNameWithoutExtension)}
                          className="form-control mt-2"
                          name="FileName"
                          validcharacter={FirmwareFileNameValidCharacters}
                          type="text"
                          placeholder="file_name"
                          as={FormikFormInput}
                        />
                      </div>                 
                    </>
                  }
                </div>
                <div className="form-group">
                  <Label
                    className="label-style col-form-label"
                    label="fota_desc"
                  />
                  <Field
                    className="form-control"
                    name="Description"
                    type="text"
                    validcharacter={["."]}
                    placeholder="fota_desc"
                    as={FormikFormTextArea}
                  />
                </div>
              </Modal.Body>
              <div className="modal-footer pt-0 border-0">
                <div className="form-group row g-0 tab-action">
                  <div className="col-12">
                    <LoaderButton
                      type="submit"
                      isLoading={props.actionLoading}
                      text={props.id > 0 ? "update" :"add"}
                    />
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={(e) => props.onModalClosed()}
                      disabled={props.actionLoading}
                    >
                      <FormattedMessage id="button_cancel" />
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddJetFotaForm;
